import { createSelector } from '@reduxjs/toolkit';
import type { MemoryEmptyStates } from 'factories/timeline';
import { DateTime } from 'luxon';
import { getIsSubscriptionExpiredOrTrialEnded } from 'redux/selectors/accounts';
import { getTimestampSnappingMinutes } from 'redux/selectors/timeline';
import { getIsUserInExperimentalFeatureGroup, getUserTimeFormat } from 'redux/selectors/user';
import { isAfterDay, isSameDay } from 'utils/date-helper';
import { getTicViewDemoMode } from './tic.demo.selectors';
import { getTimelyLegacyMemoriesRTKQueryBase } from './tic.legacy-memories.selectors';
import { getMemoryConfig } from './tic.memories.config.selectors';
import {
  getFirstMemoryStartedAt,
  getHasAnyMemorySource,
  getLastMemoryEndedAt,
  getMemoryDateRange,
  getTicMemoriesRTKQueryBase,
} from './tic.memories.selectors';

type TicMemoriesUIState = {
  isLoading: boolean;
  /** YYYY-MM-DD of the day */
  currentDate: string;
  /** Is `currentDate` "today"? */
  isToday: boolean;
  /** If timeline is empty, what message key do we render? */
  emptyState: MemoryEmptyStates | null;
  /** Draw the timeline from... */
  timelineStartDateTime: DateTime;
  /** End the timeline at... */
  timelineEndDateTime: DateTime;
  firstMemoryStartedAt: string | null;
  /** An array of hours, for timeline gutter rendering */
  hoursList: { label: string }[];
  timeFormat: 12 | 24;
  /** Timeline should scroll to this px position.. */
  /** Timeline will snap selections to this interval of minutes  */
  snapToMinutes: number;
  /** Internal users may see debug info */
  showDebugInfo: boolean;
  /** Whether to show the summary lane in the timeline */
  showTimelineSummary: boolean;
  /** Whether this account is locked to "expired" or "trial ended" */
  isSubscriptionExpiredOrTrialEnded: boolean;
};

export const getMemoriesLoadingStatus = createSelector(
  [getTicMemoriesRTKQueryBase, getTimelyLegacyMemoriesRTKQueryBase],
  (ticRTKQ, timelyRTKQ) => {
    const loadingStatus = ['uninitialized', 'pending'];
    const isLoadingPrimarySource = loadingStatus.includes(ticRTKQ.status);
    const isLoadingBackupSource = loadingStatus.includes(timelyRTKQ.status);
    const isLoading = isLoadingPrimarySource || isLoadingBackupSource;
    return { isLoading };
  }
);

/**
 * TIC's version of the `ui` for Timeline.
 */
export const getTicMemoriesUIState = createSelector(
  [
    getTicMemoriesRTKQueryBase,
    getTimelyLegacyMemoriesRTKQueryBase,
    getTicViewDemoMode,
    getMemoryDateRange,
    getHasAnyMemorySource,
    getUserTimeFormat,
    getLastMemoryEndedAt,
    getFirstMemoryStartedAt,
    getMemoryConfig,
    getTimestampSnappingMinutes,
    getIsUserInExperimentalFeatureGroup,
    getMemoriesLoadingStatus,
    getIsSubscriptionExpiredOrTrialEnded,
  ],
  (
    ticRTKQ,
    timelyRTKQ,
    viewDemoMode,
    dateRange,
    hasAnyMemorySource,
    timeFormat,
    lastMemoryEndedAt,
    firstMemoryStartedAt,
    { minutesPerBlock },
    snapToMinutes,
    showDebugInfo,
    { isLoading },
    isSubscriptionExpiredOrTrialEnded
  ): TicMemoriesUIState => {
    // Check if user has any memory sources (legacy desktop/browser OR TIC integrations)
    const hasNoMemorySources = !hasAnyMemorySource;

    // Dates
    const isToday = isSameDay(DateTime.now().toISODate(), dateRange.date);
    const isAfterToday = isAfterDay(dateRange.date, DateTime.now().toISODate());

    // Timeline
    const timelineStartDateTime = DateTime.fromISO(dateRange.startDate);
    const timelineEndDateTime = DateTime.fromISO(dateRange.endDate);

    // Determine the end of the timeline, adjusted to the last memory's end time as needed.
    const lastMemoryAt = lastMemoryEndedAt ? DateTime.fromISO(lastMemoryEndedAt) : null;
    const dynamicTimelineEnd =
      lastMemoryAt && lastMemoryAt > timelineEndDateTime ? lastMemoryAt : timelineEndDateTime;

    // Timeline "ticks" / gutter hours.
    // WIN: memoryQuery/filter `start/endDate` now control size of timeline!
    const hoursInDay = Math.round(dynamicTimelineEnd.diff(timelineStartDateTime, 'hours').hours);
    const bufferHours = 1; // Give the timeline a little extra space at the end, without loading more memories.
    const luxonFormat = timeFormat === 24 ? 'HH:mm' : 'h a';
    const hoursList = Array.from({ length: hoursInDay + bufferHours }, (_, i) => {
      return { label: timelineStartDateTime.plus({ hours: i }).toFormat(luxonFormat) };
    });

    // Are Memories actually visible (consider dummy/synthetic memories)
    const numTicMemories = ticRTKQ.data?.memories?.length || 0;
    const numTimelyMemories = timelyRTKQ.data?.length || 0;
    const hasZeroMemories = !viewDemoMode.enabled && !numTicMemories && !numTimelyMemories;

    // Determine empty state
    const emptyState =
      isLoading && hasZeroMemories
        ? 'loading-memories'
        : hasNoMemorySources && !isLoading && !viewDemoMode.enabled
          ? 'no-memory-apps'
          : isAfterToday && hasZeroMemories && !isLoading
            ? 'no-memories-on-day-in-future'
            : hasZeroMemories && !isLoading
              ? 'no-memories-on-day'
              : null;

    return {
      isLoading,
      isToday,
      currentDate: dateRange.date,
      timelineStartDateTime,
      timelineEndDateTime,
      emptyState,
      hoursList,
      timeFormat,
      firstMemoryStartedAt,
      snapToMinutes,
      showDebugInfo,
      showTimelineSummary: minutesPerBlock > 0,
      isSubscriptionExpiredOrTrialEnded,
    };
  }
);
